import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { switchMap } from 'rxjs';
import Character from '../../../models/Game Layer/character';
import Player from '../../../models/Game Layer/player';
import { AuthService } from '../../../services/auth-service';
import { GamelayerService } from '../../../services/gamelayer-service';
import { BackButtonComponent } from '../../back-button-component/back-button-component';
import { LoadingComponent } from '../../loading-component/loading-component';

type CharacterDraft = {
  name: string;
  nickname: string;
  role: string;
  type: string;
  imageUrl: string;
};

@Component({
  selector: 'app-character-create-component',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, BackButtonComponent, LoadingComponent],
  templateUrl: './character-create-component.html',
  styleUrl: './character-create-component.css',
})
export class CharacterCreateComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly gamelayerService = inject(GamelayerService);
  private readonly auth = inject(AuthService);

  readonly isCreatingCharacter = signal(false);
  readonly createCharacterError = signal('');
  readonly characterDraft = signal<CharacterDraft>({
    name: '',
    nickname: '',
    role: '',
    type: '',
    imageUrl: '',
  });

  readonly sessionId = computed(() => this.route.snapshot.queryParamMap.get('sessionId') ?? '');
  readonly sessionName = computed(() => this.route.snapshot.queryParamMap.get('sessionName') ?? '');
  readonly universeId = computed(() => this.route.snapshot.queryParamMap.get('universeId') ?? '');
  readonly universeName = computed(
    () => this.route.snapshot.queryParamMap.get('universeName') ?? '',
  );
  readonly campaignName = computed(
    () => this.route.snapshot.queryParamMap.get('campaignName') ?? '',
  );

  updateDraft<K extends keyof CharacterDraft>(key: K, value: CharacterDraft[K]): void {
    this.characterDraft.update((current) => ({
      ...current,
      [key]: value,
    }));
  }

  canCreateCharacter(): boolean {
    const draft = this.characterDraft();
    return !!(
      this.sessionId().trim() &&
      draft.name.trim() &&
      draft.nickname.trim() &&
      draft.role.trim() &&
      draft.type.trim()
    );
  }

  createCharacter(): void {
    const currentUser = this.auth.currentUser();
    if (!currentUser) {
      this.createCharacterError.set('You need to be logged in as a player to create a character.');
      return;
    }

    if (!this.canCreateCharacter()) {
      this.createCharacterError.set(
        'Name, nickname, role, type, and a valid target session are required.',
      );
      return;
    }

    this.isCreatingCharacter.set(true);
    this.createCharacterError.set('');

    const payload = {
      sessionId: this.sessionId(),
      entityId: this.universeId(),
      name: this.characterDraft().name.trim(),
      nickname: this.characterDraft().nickname.trim(),
      role: this.characterDraft().role.trim(),
      type: this.characterDraft().type.trim(),
      imageUrl: this.characterDraft().imageUrl.trim(),
      stats: [],
      inventory: [],
    };

    this.gamelayerService.postCharacter(this.sessionId(), payload)
      .pipe(
        switchMap((response) => {
          const characterData =
            response && typeof response === 'object' && 'character' in response
              ? (response as { character: unknown }).character
              : response;
          const createdCharacter = new Character(characterData);

          const playerPayload: Partial<Player> = {
            userId: currentUser.id,
            sessionId: this.sessionId(),
            characterId: createdCharacter.id,
            companions: [],
          };

          return this.gamelayerService.joinSession(this.sessionId(), playerPayload).pipe(
            switchMap(() =>
              this.router.navigate(['/characters', this.sessionId(), createdCharacter.id]).then(
                () => createdCharacter,
              ),
            ),
          );
        }),
      )
      .subscribe({
        next: () => {
          this.isCreatingCharacter.set(false);
        },
        error: (error) => {
          console.error('Error creating character:', error);
          this.createCharacterError.set(
            'The character could not be forged into the campaign right now.',
          );
          this.isCreatingCharacter.set(false);
        },
      });
  }
}
